// Shared types and interfaces for accounting views
// Extracted from stores/AccountingStore to avoid dependencies

// Enums
export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum BillStatus {
  DRAFT = "draft",
  RECEIVED = "received",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum CustomerStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum SupplierStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
  BLOCKED = "blocked",
}

export enum PaymentMethod {
  CASH = "cash",
  CHECK = "check",
  CREDIT_CARD = "credit_card",
  BANK_TRANSFER = "bank_transfer",
  OTHER = "other",
}

export enum PaymentStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum AccountType {
  ASSET = "asset",
  LIABILITY = "liability",
  EQUITY = "equity",
  REVENUE = "revenue",
  EXPENSE = "expense",
}

// Interfaces
export interface Invoice {
  id: number;
  invoiceNumber: string;
  customerId: number;
  customerName?: string; // Optional since it's not in the API response but may be populated by UI
  invoiceDate: string; // YYYY-MM-DD format
  invoiceTime: string; // HH:MM:SS format
  dueDate: string; // YYYY-MM-DD format
  dueTime: string; // HH:MM:SS format
  status: InvoiceStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  exchangeRate: number;
  paymentTerms: number; // days
  notes?: string;
  internalNotes?: string;
  createdDate: string; // YYYY-MM-DD format
  createdTime: string; // HH:MM:SS format
  updatedDate: string; // YYYY-MM-DD format
  updatedTime: string; // HH:MM:SS format
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  lineItems?: InvoiceLineItem[]; // Optional since it's not in the API response but may be populated separately
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  plantId?: string; // Plant/location ID within the company (e.g., "100" for Wenatchee)
  glAccountId?: string;
  taxable?: boolean;
}

export interface Bill {
  id: string;
  billNumber: string;
  supplierId: string;
  supplierName: string;
  vendorId: string;
  referenceNumber: string;
  billDate: string; // YYYY-MM-DD format
  issueDate: string; // YYYY-MM-DD format
  dueDate: string; // YYYY-MM-DD format
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: BillStatus;
  notes?: string;
  lineItems: BillLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface BillLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  totalAmount: number;
  total: number;
  plantId?: string; // Plant/location ID within the company (e.g., "100" for Wenatchee)
  glAccountId?: string;
}

export interface Customer {
  id: string;
  customerCode: string;
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  mobile?: string;
  website?: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  shippingAddressLine1: string;
  shippingAddressLine2?: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  shippingCountry: string;
  taxNumber: string;
  paymentTerms: number; // days
  creditLimit: number;
  isActive: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Supplier {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  taxId?: string;
  paymentTerms: string;
  balance: number;
  status: SupplierStatus;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  shippingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactPerson?: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentType: "customer_payment" | "supplier_payment";
  amount: number;
  paymentDate: string; // YYYY-MM-DD format
  paymentTime: string; // HH:MM:SS format
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  bankAccount?: string;
  currency: string;
  exchangeRate: number;
  customerId?: number | null;
  supplierId?: number | null;
  invoiceId?: number | null;
  billId?: number | null;
  status: PaymentStatus;
  notes?: string;
  createdDate: string;
  createdTime: string;
  updatedDate: string;
  updatedTime: string;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  type?: "received" | "sent";
  reference?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Re-export glAccount from api-types for Chart of Accounts
export type { glAccount as ChartOfAccount, glAccountData, glAccountPatch } from "types/api-types/financial/gl-accounts/gl-accounts.types";

export interface JournalEntry {
  id: string;
  entryNumber: string;
  entryDate: string; // YYYY-MM-DD format
  description: string;
  reference?: string;
  totalDebit: number;
  totalCredit: number;
  status: "draft" | "posted" | "reversed";
  lineItems: JournalLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface JournalLineItem {
  id: string;
  accountId: string;
  accountName: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
}

export interface AccountingSummary {
  totalReceivables: number;
  totalPayables: number;
  overdueReceivables: number;
  overduePayables: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}

export interface DashboardData {
  totalReceivables: number;
  outstandingInvoicesCount: number;
  totalPayables: number;
  outstandingBillsCount: number;
  overdueInvoicesAmount: number;
  overdueInvoicesCount: number;
  billsDueSoonAmount: number;
  billsDueSoonCount: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}

// ==================== Asset Types ====================

/**
 * Fixed asset tracking interface
 * Supports dual asset numbers: internal company tracking and CPA's asset number
 */
export interface Asset {
  id: string;
  companyId: string;
  internalAssetNumber: string; // Company's internal tracking number
  cpaAssetNumber?: string; // CPA firm's asset number for external reporting
  description: string;
  acquisitionDate: string; // YYYY-MM-DD
  acquisitionCost: number;
  currentValue?: number;
  depreciationMethod?: "straight_line" | "declining_balance" | "units_of_production";
  usefulLifeYears?: number;
  salvageValue?: number;
  glAccountCode: string;
  locationId?: string;
  status: "active" | "disposed" | "fully_depreciated";
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== Plant/Location Types ====================

/**
 * Plant/Location within a company
 * Plants represent physical locations (e.g., Wenatchee, Seattle) within a company (e.g., H2)
 */
export interface PlantLocation {
  id: string; // "100", "200", "300", "400"
  name: string; // "Wenatchee", "Seattle", "Spokane", "Pasco"
  isDefault: boolean;
  isActive?: boolean;
}

/**
 * Plant locations organized by company ID
 * Used for plant selector dropdowns on line items
 */
export const PLANTS_BY_COMPANY: Record<string, PlantLocation[]> = {
  H2: [
    { id: "100", name: "Wenatchee", isDefault: true },
    { id: "200", name: "Seattle", isDefault: false },
    { id: "300", name: "Spokane", isDefault: false },
    { id: "400", name: "Pasco", isDefault: false },
  ],
  Bridger: [{ id: "100", name: "Missoula", isDefault: true }],
  RogueValley: [{ id: "100", name: "Medford", isDefault: true }],
  CPP: [{ id: "100", name: "Main", isDefault: true }],
  PC: [{ id: "100", name: "Main", isDefault: true }],
  Sound: [{ id: "100", name: "Main", isDefault: true }],
};

/**
 * Get plants for a specific company
 */
export const getPlantsForCompany = (companyId: string): PlantLocation[] => {
  return PLANTS_BY_COMPANY[companyId] || [];
};

/**
 * Get the default plant for a company
 */
export const getDefaultPlantForCompany = (
  companyId: string
): PlantLocation | undefined => {
  const plants = getPlantsForCompany(companyId);
  return plants.find((p) => p.isDefault) || plants[0];
};

// ==================== Dimensional Accounting Types ====================

/**
 * GL Dimension for dimensional accounting approach
 * Separates "what" (account) from "where" (job/phase/cost code)
 */
export interface GLDimension {
  dimensionId: string;
  name: string;
  code: string;
  type: DimensionType;
  isRequired: boolean;
  isActive: boolean;
}

export type DimensionType =
  | "plant" // 100, 200, 300, 400
  | "job" // Project/job number
  | "phase" // CSI division
  | "cost_type" // Labor, Material, Equipment, Subcontract
  | "department"
  | "equipment"
  | "employee";

// ==================== GL Transaction Types ====================

/**
 * GL Transaction - posted ledger entry
 * Supports dimensional accounting with plant/job/phase tagging
 */
export interface GLTransaction {
  transactionId: string;
  journalEntryId?: string;
  sourceType: TransactionSourceType;
  sourceId: string;
  accountId: string;
  accountNumber: string;
  accountName: string;

  // Amounts
  debitAmount: number;
  creditAmount: number;

  // Dimensions
  plantId?: string;
  jobId?: string;
  phaseCode?: string;
  costType?: string;
  departmentId?: string;
  equipmentId?: string;
  employeeId?: string;

  // Vendor/Customer (populated from source documents)
  vendorId?: string;
  customerId?: string;

  // Metadata
  transactionDate: string;
  postingDate: string;
  periodId: string;
  description: string;
  reference?: string;

  // Audit
  createdAt: string;
  createdBy: string;
  isReversed: boolean;
  reversalId?: string;
}

export type TransactionSourceType =
  | "journal_entry"
  | "invoice"
  | "bill"
  | "payment"
  | "payroll"
  | "inventory"
  | "wip_adjustment"
  | "depreciation"
  | "recurring";

// ==================== Account Balance Types ====================

/**
 * Account Balance - period snapshot for reporting
 */
export interface AccountBalance {
  balanceId: string;
  accountId: string;
  periodId: string;
  plantId?: string; // For plant-level balances

  beginningBalance: number;
  periodDebits: number;
  periodCredits: number;
  endingBalance: number;

  // For budget comparison
  budgetAmount?: number;
  varianceAmount?: number;
  variancePercent?: number;
}

// ==================== Fiscal Period Types ====================

/**
 * Fiscal Period with module-level lock controls
 */
export interface FiscalPeriod {
  periodId: string;
  periodNumber: number; // 1-13
  periodName: string; // "January 2026"
  fiscalYear: number;
  startDate: string;
  endDate: string;

  // Lock status by module
  isGLLocked: boolean;
  isAPLocked: boolean;
  isARLocked: boolean;
  isPayrollLocked: boolean;

  lockedAt?: string;
  lockedBy?: string;

  // Year-end flags
  isYearEnd: boolean;
  isAdjustmentPeriod: boolean;
}

export type LockableModule = "gl" | "ap" | "ar" | "payroll";

// ==================== Enhanced Chart of Accounts Types ====================

/**
 * Enhanced GL Account with dimensional defaults
 */
export interface EnhancedGLAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  parentAccountId?: string;
  normalBalance: "debit" | "credit";
  isActive: boolean;
  isHeader: boolean; // Subtotal accounts
  isBankAccount: boolean;
  bankAccountNumber?: string;
  balance: number;
  description?: string;

  // Precast-specific categorization
  costCategory?: PrecastCostCategory;

  // Dimension defaults
  defaultPlantId?: string;
  allowedPlantIds?: string[];
  requiresJob: boolean;
  requiresPhase: boolean;
  requiresCostType: boolean;

  createdAt: string;
  updatedAt: string;
}

export type PrecastCostCategory =
  | "direct_materials"
  | "direct_labor"
  | "manufacturing_overhead"
  | "selling_expense"
  | "administrative_expense"
  | "other_income_expense";

// ==================== Precast Job Cost Types ====================

/**
 * Job Cost Allocation for precast concrete projects
 */
export interface JobCostAllocation {
  allocationId: string;
  jobId: string;
  jobNumber: string;
  jobName: string;

  // CSI MasterFormat division
  phaseCode: string;
  phaseDescription: string;

  // Cost types
  costType: PrecastCostType;

  // GL mapping
  glAccountId: string;

  // Amounts
  budgetAmount: number;
  committedAmount: number;
  actualAmount: number;
  remainingAmount: number;
}

export type PrecastCostType =
  | "raw_materials" // Concrete, steel, aggregates
  | "forms" // Form costs
  | "embeds" // Embedded items
  | "labor_production" // Production labor
  | "labor_finishing" // Finishing labor
  | "equipment" // Equipment costs
  | "freight_inbound" // Incoming materials
  | "freight_outbound" // Delivery
  | "subcontract" // Sub work
  | "overhead" // Allocated overhead
  | "other";

// ==================== WIP Schedule Types ====================

/**
 * WIP Schedule Entry for percent-complete accounting
 */
export interface WIPScheduleEntry {
  wipId: string;
  jobId: string;
  periodId: string;

  // Contract values
  originalContractAmount: number;
  approvedChanges: number;
  revisedContractAmount: number;

  // Cost tracking
  totalCostToDate: number;
  estimatedCostToComplete: number;
  estimatedTotalCost: number;

  // Billing
  totalBilledToDate: number;
  retentionHeld: number;

  // WIP calculations
  percentComplete: number; // Cost-based
  earnedRevenue: number;
  overUnderBilling: number;

  // GL adjustment
  wipAdjustmentAmount: number;
  wipGlAccountId: string;
}

// ==================== Reconciliation Types ====================

/**
 * Bank/Account Reconciliation
 */
export interface Reconciliation {
  reconciliationId: string;
  accountId: string;
  periodId: string;
  status: ReconciliationStatus;

  // Statement values
  statementDate: string;
  statementBalance: number;

  // GL values
  glBeginningBalance: number;
  glEndingBalance: number;

  // Reconciliation
  clearedDebits: number;
  clearedCredits: number;
  outstandingDebits: number;
  outstandingCredits: number;
  difference: number;

  // Audit
  reconciledBy?: string;
  reconciledAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export type ReconciliationStatus =
  | "not_started"
  | "in_progress"
  | "pending_review"
  | "approved"
  | "locked";

/**
 * Reconciliation Match - links GL transactions to statement lines
 */
export interface ReconciliationMatch {
  matchId: string;
  reconciliationId: string;
  matchType: "auto" | "manual" | "rule_based";

  // GL side
  glTransactionIds: string[];
  glTotal: number;

  // Statement side
  statementLineIds: string[];
  statementTotal: number;

  // Match quality
  confidence: number; // 0-100
  matchReason: string;

  // Status
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
}

/**
 * Bank Statement Line for reconciliation
 */
export interface BankStatementLine {
  lineId: string;
  reconciliationId: string;
  statementDate: string;
  description: string;
  reference?: string;
  amount: number;
  isDebit: boolean;
  isMatched: boolean;
  matchId?: string;
  importedAt: string;
}

// ==================== Audit Trail Types ====================

/**
 * Audit Log Entry with field-level change tracking
 */
export interface AuditLogEntry {
  auditId: string;
  entityType: string;
  entityId: string;
  action: "create" | "update" | "delete" | "post" | "reverse";

  // Change details
  changedFields: FieldChange[];

  // Context
  userId: string;
  userName: string;
  timestamp: string;
  sessionId: string;
  ipAddress?: string;

  // Transaction context
  journalEntryId?: string;
  periodId?: string;
}

/**
 * Field-level change record
 */
export interface FieldChange {
  fieldName: string;
  oldValue: unknown;
  newValue: unknown;
  displayOldValue: string;
  displayNewValue: string;
}

// ==================== Command Palette Types ====================

/**
 * Command Palette Item for keyboard navigation
 */
export interface CommandPaletteItem {
  id: string;
  label: string;
  shortcut?: string;
  category: CommandCategory;
  action: () => void;
  icon?: string;
  keywords?: string[]; // For fuzzy search
}

export type CommandCategory =
  | "navigation"
  | "create"
  | "reports"
  | "search"
  | "settings";

// ==================== Report Configuration Types ====================

/**
 * Financial Report Configuration
 */
export interface ReportConfiguration {
  reportId: string;
  reportType: FinancialReportType;
  name: string;

  // Plant options
  plantConfig: {
    enabled: boolean;
    mode: "all" | "selected" | "each";
    showPlantColumn: boolean;
    subtotalByPlant: boolean;
    splitByPlant: boolean;
  };

  // Date configuration
  dateConfig: {
    type: "period" | "date_range" | "as_of";
    allowComparison: boolean;
  };

  // Column configuration
  columns: ReportColumn[];

  // Drill-down configuration
  drillDown: {
    enabled: boolean;
    levels: DrillDownLevel[];
  };

  // Export options
  exportFormats: ("pdf" | "excel" | "csv")[];
}

export type FinancialReportType =
  | "trial_balance"
  | "income_statement"
  | "balance_sheet"
  | "general_ledger"
  | "account_activity"
  | "wip_schedule"
  | "job_cost_summary";

export interface ReportColumn {
  key: string;
  label: string;
  width?: number;
  align?: "left" | "center" | "right";
  format?: "text" | "currency" | "percent" | "date" | "number";
  isSummable?: boolean;
  isVisible?: boolean;
}

export interface DrillDownLevel {
  name: string;
  groupBy: string;
  showDetail: boolean;
  nextLevel?: string;
}

// ==================== GL View State Types ====================

export type GLViewMode = "summary" | "transactions" | "detail";

export type DateRangeType =
  | "this_period"
  | "last_period"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "ytd"
  | "custom";

export type GLSearchField = "description" | "reference" | "account" | "job";

// ==================== Financial Reports Types ====================

/**
 * Report period granularity for financial reports
 */
export enum ReportPeriod {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  YEARLY = "yearly",
  CUSTOM = "custom",
}

/**
 * Aging bucket breakdown for AR/AP aging reports
 */
export interface AgingBucket {
  current: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
}

/**
 * Job entity for job costing and profitability reports
 */
export interface Job {
  id: string;
  jobNumber: string;
  jobName: string;
  customerName?: string;
  customerId?: string;
  estimatedCost?: number;
  actualCost?: number;
  status: string;
  startDate?: string;
  completedDate?: string;
  actualHours?: number;
  budgetedHours?: number;
}

/**
 * Cost allocation entry for job cost variance tracking.
 * Simplified alias distinct from JobCostAllocation.
 */
export interface CostAllocation {
  id?: string;
  jobId: string;
  category: string;
  totalCost: number;
}

/**
 * Production cost entry for COGS calculation
 */
export interface ProductionCost {
  id?: string;
  costDate: string;
  category: string;
  totalCost: number;
}

/**
 * Labor entry for direct labor cost tracking
 */
export interface LaborEntry {
  id?: string;
  workDate: string;
  totalLabor: number;
}

/**
 * Equipment cost entry for depreciation and equipment expense tracking
 */
export interface EquipmentCost {
  id?: string;
  usageDate: string;
  costType: string;
  totalCost: number;
}

// ==================== Financial Statement Report Types ====================

/**
 * Income Statement (Profit & Loss)
 */
export interface IncomeStatement {
  id: string;
  period: ReportPeriod;
  startDate: string;
  endDate: string;
  revenue: {
    sales: number;
    otherIncome: number;
    totalRevenue: number;
  };
  costOfGoodsSold: {
    directMaterials: number;
    directLabor: number;
    manufacturingOverhead: number;
    totalCOGS: number;
  };
  grossProfit: number;
  grossProfitMargin: number;
  operatingExpenses: {
    selling: number;
    administrative: number;
    depreciation: number;
    other: number;
    totalOperatingExpenses: number;
  };
  operatingIncome: number;
  operatingMargin: number;
  otherExpenses: number;
  netIncome: number;
  netProfitMargin: number;
}

/**
 * Balance Sheet (Statement of Financial Position)
 */
export interface BalanceSheet {
  id: string;
  asOfDate: string;
  assets: {
    currentAssets: {
      cash: number;
      accountsReceivable: number;
      inventory: number;
      prepaidExpenses: number;
      totalCurrentAssets: number;
    };
    fixedAssets: {
      propertyPlantEquipment: number;
      accumulatedDepreciation: number;
      netFixedAssets: number;
    };
    totalAssets: number;
  };
  liabilities: {
    currentLiabilities: {
      accountsPayable: number;
      accruedExpenses: number;
      currentPortionLongTermDebt: number;
      totalCurrentLiabilities: number;
    };
    longTermLiabilities: {
      longTermDebt: number;
      otherLiabilities: number;
      totalLongTermLiabilities: number;
    };
    totalLiabilities: number;
  };
  equity: {
    commonStock: number;
    retainedEarnings: number;
    totalEquity: number;
  };
  totalLiabilitiesAndEquity: number;
}

/**
 * Cash Flow Statement
 */
export interface CashFlowStatement {
  id: string;
  period: ReportPeriod;
  startDate: string;
  endDate: string;
  operatingActivities: {
    netIncome: number;
    depreciation: number;
    accountsReceivableChange: number;
    inventoryChange: number;
    accountsPayableChange: number;
    netCashFromOperations: number;
  };
  investingActivities: {
    equipmentPurchases: number;
    assetSales: number;
    netCashFromInvesting: number;
  };
  financingActivities: {
    debtProceeds: number;
    debtRepayments: number;
    dividendsPaid: number;
    netCashFromFinancing: number;
  };
  netCashChange: number;
  beginningCash: number;
  endingCash: number;
}

// ==================== Profitability Report Types ====================

/**
 * Job-level profitability report entry
 */
export interface JobProfitability {
  jobId: string;
  jobNumber: string;
  jobName: string;
  customerName?: string;
  revenue: number;
  totalCost: number;
  grossProfit: number;
  grossProfitMargin: number;
  estimatedCost: number;
  costVariance: number;
  costVariancePercent: number;
  status: string;
  completedDate?: string;
}

/**
 * Customer-level profitability report entry
 */
export interface CustomerProfitability {
  customerId: string;
  customerName: string;
  totalRevenue: number;
  totalCost: number;
  grossProfit: number;
  grossProfitMargin: number;
  jobCount: number;
  averageJobValue: number;
  averageJobProfit: number;
}

/**
 * Job cost variance analysis entry
 */
export interface JobCostVariance {
  jobId: string;
  jobNumber: string;
  jobName: string;
  budgetedCost: number;
  actualCost: number;
  variance: number;
  variancePercent: number;
  materialVariance: number;
  laborVariance: number;
  overheadVariance: number;
  equipmentVariance: number;
  status: string;
  forecastToComplete: number;
  estimatedFinalCost: number;
}

// ==================== Aging Report Types ====================

/**
 * Accounts Receivable Aging Report
 */
export interface ARAgingReport {
  id: string;
  asOfDate: string;
  customerAging: Array<{
    customerId: string;
    customerName: string;
    aging: AgingBucket;
  }>;
  totalAging: AgingBucket;
}

/**
 * Accounts Payable Aging Report
 */
export interface APAgingReport {
  id: string;
  asOfDate: string;
  supplierAging: Array<{
    supplierId: string;
    supplierName: string;
    aging: AgingBucket;
  }>;
  totalAging: AgingBucket;
}

// ==================== Cash Flow Forecast Types ====================

/**
 * Cash flow forecast with daily projections
 */
export interface CashFlowForecast {
  id: string;
  forecastDate: string;
  forecastPeriod: number;
  currentCash: number;
  projectedReceipts: Array<{
    date: string;
    amount: number;
    source: string;
  }>;
  projectedPayments: Array<{
    date: string;
    amount: number;
    payee: string;
  }>;
  dailyProjections: Array<{
    date: string;
    receipts: number;
    payments: number;
    netCash: number;
    runningBalance: number;
  }>;
  minimumCash: number;
  minimumCashDate: string;
}

// ==================== Executive KPI Types ====================

/**
 * Executive KPI dashboard data
 */
export interface ExecutiveKPIs {
  period: ReportPeriod;
  startDate: string;
  endDate: string;
  financial: {
    revenue: number;
    revenueGrowth: number;
    grossProfit: number;
    grossProfitMargin: number;
    netIncome: number;
    netProfitMargin: number;
    ebitda: number;
    ebitdaMargin: number;
  };
  operational: {
    jobsCompleted: number;
    jobsInProgress: number;
    averageJobDuration: number;
    onTimeDeliveryRate: number;
    productionEfficiency: number;
  };
  cashFlow: {
    currentCash: number;
    cashGrowth: number;
    daysOfCash: number;
    operatingCashFlow: number;
  };
  receivables: {
    totalAR: number;
    overdueAR: number;
    dso: number;
    collectionEfficiency: number;
  };
  payables: {
    totalAP: number;
    overdueAP: number;
    dpo: number;
  };
}
